// src/components/Reports/BusinessInsightDashboard.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { config } from "../../Helpers/env";
import { LoadingGif } from "../Utils/Loader";
import PageTitle from "../Utils/smsTitle";
import { SubHeader } from "../../Containers/SubHeader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faDollarSign,
  faShoppingCart,
  faPiggyBank,
  faArrowUp,
  faArrowDown,
  faSyncAlt,
} from "@fortawesome/free-solid-svg-icons";
import { format } from "date-fns";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { Link } from "react-router-dom";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";

// -----------------------------
// Back Button
// -----------------------------
const BackButton = ({ to, text }) => (
  <Link to={to} className="text-decoration-none">
    <button className="btn btn-outline-dark d-flex "
          style={{ minWidth: "140px" , height: "40px"}}>
      <ArrowBackIcon fontSize="small" /> Back to {text}
    </button>
  </Link>
);

const BusinessInsightDashboard = () => {
  const [filter, setFilter] = useState({
    start_date: format(
      new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      "yyyy-MM-dd"
    ),
    end_date: format(new Date(), "yyyy-MM-dd"),
  });
  const [period, setPeriod] = useState({ ...filter });
  const [summary, setSummary] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [summaryRes, chartRes] = await Promise.all([
        axios.get(`${config.smsUrl}/creport/insight_report`, { params: period }),
        axios.get(`${config.smsUrl}/creport/insight_graph_data`, { params: period }),
      ]);
      setSummary(summaryRes.data);
      setChartData(chartRes.data.chart_data || []);
    } catch (err) {
      console.error("Failed to load dashboard:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [period]);

  const handleFilter = () => {
    setPeriod({ ...filter });
  };

  if (loading)
    return (
      <div className="text-center py-5">
        <LoadingGif />
      </div>
    );
  if (!summary)
    return (
      <div className="text-center py-5 text-danger">Failed to load data</div>
    );

  const { revenue, expenses, profit } = summary;
  const isProfit = profit.status === "PROFIT";

  // -----------------------------
  // KPI Card Component
  // -----------------------------
  const KPICard = ({ title, sspValue, usdValue, icon, gradient, textColor }) => (
    <div className="col-lg-3 col-md-6">
      <div
        className={`card shadow-lg h-100 text-white border-0`}
        style={{ background: gradient }}
      >
        <div className="card-body text-center p-4">
          <FontAwesomeIcon icon={icon} size="3x" className="mb-3 opacity-75" />
          <h5 className="fw-bold">{title}</h5>
          <h2 className="display-6 fw-bold mb-1">{sspValue}</h2>
          <p className={`mb-0 ${textColor}`}>{usdValue}</p>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <PageTitle title="Business Insight Dashboard" />
      <SubHeader title="CEO Dashboard" subTitle="Real-Time Financial Intelligence">
        <FontAwesomeIcon icon={faChartLine} />
      </SubHeader>

      <div className="container-fluid mt-4">
        {/* FILTER */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body">
            <div className="row align-items-center g-3">
              <div className="col-md-3">
                <BackButton to="/reports/generalreports" text="Reports" />
              </div>
              <div className="col-md-3">
                <label className="form-label small fw-bold">Start Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={filter.start_date}
                  onChange={(e) =>
                    setFilter({ ...filter, start_date: e.target.value })
                  }
                />
              </div>
              <div className="col-md-3">
                <label className="form-label small fw-bold">End Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={filter.end_date}
                  onChange={(e) =>
                    setFilter({ ...filter, end_date: e.target.value })
                  }
                />
              </div>
              <div className="col-md-3 d-flex flex-column justify-content-end">
                <button
                  onClick={handleFilter}
                  className="btn btn-primary btn-lg w-100"
                >
                  <FontAwesomeIcon
                    icon={faSyncAlt}
                    spin={loading}
                    className="me-2"
                  />
                  Apply Filter
                </button>
                <small className="text-muted mt-1 text-end">
                  Updated: {summary.generated_at}
                </small>
              </div>
            </div>
          </div>
        </div>

        {/* KPI CARDS */}
        <div className="row g-4 mb-5">
          <KPICard
            title="Revenue"
            sspValue={`SSP ${revenue.ssp.toLocaleString()}`}
            usdValue={`$${revenue.usd.toLocaleString()}`}
            icon={faDollarSign}
            gradient="linear-gradient(135deg, #667eea, #764ba2)"
            textColor="text-warning"
          />
          <KPICard
            title="Expenses"
            sspValue={`SSP ${expenses.ssp.toLocaleString()}`}
            usdValue={`$${expenses.usd.toLocaleString()}`}
            icon={faShoppingCart}
            gradient="linear-gradient(135deg, #f093fb, #f5576c)"
            textColor="text-warning"
          />
          <KPICard
            title={`Net ${profit.status}`}
            sspValue={`SSP ${profit.ssp.toLocaleString()}`}
            usdValue={`$${profit.usd.toLocaleString()}`}
            icon={isProfit ? faArrowUp : faArrowDown}
            gradient={isProfit ? "linear-gradient(135deg, #10b981, #047857)" : "linear-gradient(135deg, #ef4444, #b91c1c)"}
            textColor="text-white"
          />
          <KPICard
            title="Next Month Target"
            sspValue={`SSP ${(revenue.ssp * 1.15).toLocaleString()}`}
            usdValue="+15% Growth"
            icon={faPiggyBank}
            gradient="linear-gradient(135deg, #4facfe, #00f2fe)"
            textColor="text-warning"
          />
        </div>

        {/* CHARTS */}
        <div className="row g-4 mb-5">
          {/* SSP LINE CHART */}
          <div className="col-lg-6">
            <div className="card border-0 shadow-lg h-100">
              <div className="card-header bg-primary text-white text-center py-3">
                <h5 className="mb-0 fw-bold">SSP Trend</h5>
              </div>
              <div className="card-body p-3">
                {chartData.length === 0 ? (
                  <div className="text-center py-5 text-muted">No SSP data</div>
                ) : (
                  <ResponsiveContainer width="100%" height={350}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="4 4" stroke="#e0e0e0" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={(v) => `SSP ${v.toLocaleString()}`} />
                      <Legend />
                      <Line type="monotone" dataKey="revenue_ssp" stroke="#10b981" strokeWidth={3} name="Revenue" />
                      <Line type="monotone" dataKey="expense_ssp" stroke="#ef4444" strokeWidth={3} name="Expense" />
                      <Line type="monotone" dataKey="profit_ssp" stroke="#3b82f6" strokeWidth={3} name="Profit" />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>

          {/* USD LINE CHART */}
          <div className="col-lg-6">
            <div className="card border-0 shadow-lg h-100">
              <div className="card-header bg-success text-white text-center py-3">
                <h5 className="mb-0 fw-bold">USD Trend</h5>
              </div>
              <div className="card-body p-3">
                {chartData.length === 0 ? (
                  <div className="text-center py-5 text-muted">No USD data</div>
                ) : (
                  <ResponsiveContainer width="100%" height={350}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="4 4" stroke="#e0e0e0" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={(v) => `$${v.toLocaleString()}`} />
                      <Legend />
                      <Line type="monotone" dataKey="revenue_usd" stroke="#10b981" strokeWidth={3} name="Revenue" />
                      <Line type="monotone" dataKey="expense_usd" stroke="#ef4444" strokeWidth={3} name="Expense" />
                      <Line type="monotone" dataKey="profit_usd" stroke="#3b82f6" strokeWidth={3} name="Profit" />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* PROFIT PIE + MESSAGE */}
        <div className="row g-4">
          <div className="col-lg-4">
            <div className="card border-0 shadow-lg h-100">
              <div className="card-header bg-warning text-dark">
                <h5 className="mb-0">Profit Distribution (SSP)</h5>
              </div>
              <div className="card-body">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: "Profit", value: Math.max(profit.ssp, 0) },
                        { name: "Expenses", value: expenses.ssp },
                        { name: "Revenue", value: revenue.ssp },
                      ]}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="value"
                      label
                    >
                      <Cell fill="#10b981" />
                      <Cell fill="#ef4444" />
                      <Cell fill="#94a3b8" />
                    </Pie>
                    <Tooltip formatter={(v) => `SSP ${v.toLocaleString()}`} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="col-lg-8">
            <div className="text-center py-5 bg-dark text-white rounded-4 h-100 d-flex flex-column justify-content-center">
              <h1
                className={`display-1 fw-bold ${
                  isProfit ? "text-success" : "text-danger"
                }`}
              >
                {isProfit ? "YOU ARE WINNING BOSS!" : "TIME TO DOMINATE!"}
              </h1>
              <p className="display-6 mt-3">
                Your empire is growing. Keep crushing it.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BusinessInsightDashboard;
